import type React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { buildInstallUrl } from "../../services/AttributionService";
import { HF } from "./homeFeedUi";
import { PENNY_MEALS_FOOTER_LABEL, PENNY_MEALS_URL } from "../../seo/pennyMeals";

const GOLD = "#FFD24A";
const FOOTER_TEXT = "var(--hf-footer-text)";
const FOOTER_MUTED = "var(--hf-footer-muted)";
const LINK = "no-underline transition-colors hover:text-white";

const COLUMNS: { title: string; links: { label: string; to: string; external?: boolean; rel?: string }[] }[] = [
  {
    title: "Shop",
    links: [
      { label: "Walmart.com deals", to: "/walmart" },
      { label: "Amazon deals", to: "/amazon" },
      { label: "Target.com deals", to: "/target" },
      { label: "Best Buy deals", to: "/best-buy" },
    ],
  },
  {
    title: "Learn",
    links: [
      { label: "Clearance guides", to: "/guides" },
      { label: "Free tools", to: "/tools" },
      { label: "Join the Discord", to: "https://discord.gg/3DTMngFYb9", external: true },
      // Sister app; a followed link on purpose (no nofollow, keeps the referrer).
      { label: PENNY_MEALS_FOOTER_LABEL, to: PENNY_MEALS_URL, external: true, rel: "noopener" },
    ],
  },
  {
    title: "Help",
    links: [
      { label: "Support", to: "/support" },
      { label: "Contact us", to: "/contact-us" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

/** Footer for the logged-out deals pages: store links, help, legal and the apps. */
export default function SiteFooter() {
  return (
    <footer
      className="mt-12 border-t"
      style={{ background: "var(--hf-footer)", borderColor: "var(--hf-footer-border)", color: FOOTER_TEXT, fontFamily: HF.font }}
      data-testid="site-footer"
    >
      <div className="mx-auto flex max-w-[1360px] flex-col gap-10 px-5 pb-8 pt-12 sm:px-6 lg:flex-row lg:gap-16">
        <div className="flex max-w-[340px] flex-col gap-4">
          <Link to="/" className="flex items-center gap-2.5 text-white no-underline" aria-label="DealHawk home">
            <img src="/hawk_icon_white.svg" alt="" width={30} height={30} className="h-[30px] w-[30px]" />
            <span className="text-[20px] font-extrabold tracking-[-0.4px]">DealHawk</span>
          </Link>
          <p className="m-0 text-[14px] leading-[21px]">
            Clearance at the stores near you, with the shelf price, aisle and how many are left. Plus online deals from 60+ stores.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <StoreBadge href={buildInstallUrl("ios")} caption="Download on the" store="App Store" icon={<AppleLogo />} />
            <StoreBadge href={buildInstallUrl("android")} caption="Get it on" store="Google Play" icon={<PlayLogo />} />
          </div>
        </div>
        <nav aria-label="Footer" className="grid flex-1 grid-cols-2 gap-8 sm:grid-cols-3">
          {COLUMNS.map((column) => (
            <div key={column.title} className="flex flex-col gap-3">
              <h2 className="m-0 text-[12.5px] font-extrabold tracking-[0.1em]" style={{ color: GOLD }}>{column.title.toUpperCase()}</h2>
              {column.links.map((link) =>
                link.external ? (
                  <a key={link.label} href={link.to} target="_blank" rel={link.rel ?? "noopener noreferrer"} className={`text-[14px] ${LINK}`} style={{ color: FOOTER_TEXT }}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.label} to={link.to} className={`text-[14px] ${LINK}`} style={{ color: FOOTER_TEXT }}>
                    {link.label}
                  </Link>
                ),
              )}
            </div>
          ))}
        </nav>
      </div>
      <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="mx-auto flex max-w-[1360px] flex-wrap items-center gap-x-6 gap-y-3 px-5 py-5 text-[12.5px] sm:px-6" style={{ color: FOOTER_MUTED }}>
          <span>© {new Date().getFullYear()} DealHawk</span>
          <span>Prices and stock change often. Check the store before you go.</span>
          <AppearanceToggle />
        </div>
      </div>
    </footer>
  );
}

const APPEARANCE_OPTIONS = [
  { value: "light", label: "Light" },
  { value: "dim", label: "Dim" },
  { value: "black", label: "Black" },
] as const;

/** Light / Dim / Black switch for the logged-out pages; the choice is shared with the app (ThemeContext). */
function AppearanceToggle() {
  const { theme, setTheme, darkStyle, setDarkStyle } = useTheme();
  const current = theme === "light" ? "light" : darkStyle === "gray" ? "dim" : "black";
  const pick = (value: (typeof APPEARANCE_OPTIONS)[number]["value"]) => {
    if (value !== "light") setDarkStyle(value === "dim" ? "gray" : "black");
    setTheme(value === "light" ? "light" : "dark");
  };
  return (
    <div className="flex items-center gap-2.5 sm:ml-auto" data-testid="appearance-toggle">
      <span id="footer-appearance-label" className="font-semibold">Appearance</span>
      <div
        role="group"
        aria-labelledby="footer-appearance-label"
        className="flex rounded-full border p-[3px]"
        style={{ borderColor: "rgba(255,255,255,0.16)" }}
      >
        {APPEARANCE_OPTIONS.map((option) => {
          const selected = current === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => pick(option.value)}
              className="flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD24A]"
              style={selected ? { background: "rgba(255,255,255,0.14)", color: "#FFFFFF" } : { color: FOOTER_TEXT }}
            >
              {option.value === "light" ? <SunIcon /> : <MoonIcon />}
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SunIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

/** App Store / Google Play badge: the store's mark plus the standard two-line label. */
export function StoreBadge({ href, caption, store, icon }: { href: string; caption: string; store: string; icon: React.ReactNode }) {
  return (
    <a
      href={href}
      className="flex h-12 items-center gap-2.5 rounded-[12px] border bg-black px-3.5 text-white no-underline transition-colors hover:bg-[#1A1A1A]"
      style={{ borderColor: "rgba(255,255,255,0.28)" }}
    >
      {icon}
      <span className="flex flex-col leading-none">
        <span className="text-[10.5px] font-medium opacity-85">{caption}</span>
        <span className="mt-[3px] text-[17px] font-semibold tracking-[-0.2px]">{store}</span>
      </span>
    </a>
  );
}

export function AppleLogo() {
  return (
    <svg width="22" height="26" viewBox="0 0 814 1000" fill="currentColor" aria-hidden="true">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

export function PlayLogo() {
  return (
    <svg width="22" height="24" viewBox="0 0 466 511.98" aria-hidden="true">
      <path fill="#EA4335" d="M199.9 237.8 1.8 449.7c6.1 22.6 26.8 39.3 51.4 39.3 9.8 0 19.1-2.7 27-7.3l223.6-128.4-104-115.5z" />
      <path fill="#FBBC04" d="M400.6 202.6 303.9 147 195 243.4l109.3 109.2 96.1-55.2c16.8-9.1 28.3-26.8 28.3-47.2-.1-20.4-11.4-38.2-28.1-47.6z" />
      <path fill="#4285F4" d="M1.8 62.4C1.2 64.7.8 67 .5 69.5v373c.3 2.5.7 4.8 1.3 7.2l205.2-204.6L1.8 62.4z" />
      <path fill="#34A853" d="M201.4 256 304.6 153.2 80.5 24.6C72.4 19.8 62.9 17 52.8 17 28.2 17 7.5 33.7 1.8 56.4L201.4 256z" />
    </svg>
  );
}
